import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NgSelectOption,
  Validators,
} from "@angular/forms";
import { NgbTime } from "@ng-bootstrap/ng-bootstrap/timepicker/ngb-time";
import { NgSelectComponent } from "@ng-select/ng-select";
import { OrderCreateRequestUpdate } from "components/order-management/order-components-interface";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
} from "ionic-angular";
import { ViewController } from "ionic-angular/navigation/view-controller";
import { MAREditedAction } from "models/order-management/MAREditedAction";
import {
  Action,
  DropdownItem,
  OrderComponentPropertyConfiguration,
  OrderPatchRequest,
  OrderResponse,
} from "models/order-management/models";
import * as moment from "moment-timezone";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { take } from "rxjs/operators";

@Component({
  selector: "add-edit-mar-details",
  templateUrl: "add-edit-mar-details.html",
})
export default class AddEditMARDetails {
  @Input("hide-arrow") hideArrow: boolean = true;

  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();

  @ViewChild("selectorTimes") selectorTimes: NgSelectComponent;
  @ViewChild("selectorDaysOfTheWeek") selectorDaysOfTheWeek: NgSelectComponent;

  formAddEditMARDetails: FormGroup;
  listDaysOfTheWeek: Array<DropdownItem>;
  order: OrderResponse;
  patientChartId: number;
  patientId: number;
  userDisplayName: string;
  spinner: any = null;
  spinnerCounter: number = 0;
  MARDetails: boolean = false;

  /**************************************************************************
   * Component Properties                                                   *
   **************************************************************************/
  componentProperties: Array<OrderComponentPropertyConfiguration>;
  dayOfTheWeekSortValueByDay: { [key: string]: number } = {};
  propertiesAdministrationFrequency: OrderComponentPropertyConfiguration;
  propertiesDaysOfTheWeek: OrderComponentPropertyConfiguration;
  propertiesNotes: OrderComponentPropertyConfiguration;
  propertiesTimes: OrderComponentPropertyConfiguration;

  /**************************************************************************
   * Internal State                                                         *
   **************************************************************************/
  ngbTime: NgbTime;
  timesComponent: OrderComponentPropertyConfiguration;
  upsertAddEditMARDetails: MAREditedAction;
  areSelectedTimesOrSelectedDaysOfTheWeekPopulated: boolean = false;
  isAdministrationFrequencyPopulated: boolean = false;

  /**************************************************************************
   * Fields                                                                 *
   **************************************************************************/
  selectTimesSet: Set<string> = new Set();

  public constructor(
    private alertCtrl: AlertController,
    private formBuilder: FormBuilder,
    private loadingCtrl: LoadingController,
    private orderManagementDataService: OrderManagementDataService,
    private toastController: ToastController,
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController
  ) {
    // Assign Initial State
    this.order = this.navParams.data.order;
    this.patientChartId = this.navParams.data.patientChartId;
    this.patientId = this.navParams.data.patientId;
    this.userDisplayName = this.navParams.data.userDisplayName;

    // Gather Properties
    this.componentProperties = this.navParams.data.componentProperties;
    this.propertiesAdministrationFrequency = this.findOrderComponentByProperty(
      this.componentProperties,
      "name",
      "MARFrequency"
    );
    this.propertiesDaysOfTheWeek = this.findOrderComponentByProperty(
      this.componentProperties,
      "name",
      "MARDaysOfTheWeek"
    );
    this.propertiesNotes = this.findOrderComponentByProperty(
      this.componentProperties,
      "name",
      "MARNotes"
    );
    this.propertiesTimes = this.findOrderComponentByProperty(
      this.componentProperties,
      "name",
      "MARTimes"
    );

    // Sort Values By Day For "Days Of The Week Dropdown"
    // Given the configuration, assign each day of the week a sort value,
    // e.g., { SUNDAY: 0, MONDAY: 1, etc. }
    this.dayOfTheWeekSortValueByDay =
      this.propertiesDaysOfTheWeek.dropdownItems!.reduce<{
        [key: string]: number;
      }>((acc, val, index) => {
        acc[val.value!] = index;
        return acc;
      }, {});

    // Form
    this.formAddEditMARDetails = new FormGroup({
      additionalNotes: this.formBuilder.control(null),
      needsToBeAddedToMAR: this.formBuilder.control(
        null,
        Validators.requiredTrue
      ),
      selectedAdministrationFrequency: this.formBuilder.control(null),
      selectedDaysOfTheWeek: this.formBuilder.control([]),
      selectedTimes: this.formBuilder.control([]),
      time: this.formBuilder.control(null),
    });
  }

  /**************************************************************************
   * General Methods                                                        *
   **************************************************************************/

  findOrderComponentByProperty(
    items: Array<OrderComponentPropertyConfiguration>,
    propertyName: string,
    propertyValue: string
  ): OrderComponentPropertyConfiguration {
    for (const item of items) {
      if (item[propertyName] === propertyValue) {
        return item;
      }
    }
  }

  presentToast(message) {
    let toast = this.toastController.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  // Clear all MARDetail fields and empty request
  clearMARDetails(deleteAction = false) {
    this.MARDetails = false;
    this.ngbTime = undefined;
    this.selectTimesSet = new Set();
    this.formAddEditMARDetails.controls["selectedTimes"].setValue([]);
    this.formAddEditMARDetails.controls["additionalNotes"].setValue("");
    this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].setValue([]);
    this.formAddEditMARDetails.controls[
      "selectedAdministrationFrequency"
    ].setValue(null);
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    this.setIsAdministrationFrequencyPopulated();
  }

  // A click to add or remove details
  toggleMARDetails() {
    if (this.MARDetails) {
      this.MARDetails = false;
      this.clearMARDetails();
    } else {
      this.MARDetails = true;
    }
  }

  /**************************************************************************
   * Form Control Validations
   **************************************************************************/

  formCanBeSaved(): boolean {
    return this.formAddEditMARDetails.valid;
  }

  toggleSelectors(): void {
    if (this.isAdministrationFrequencyPopulated) {
      this.formAddEditMARDetails.get("selectedTimes").disable();
      this.formAddEditMARDetails.get("selectedDaysOfTheWeek").disable();
    } else {
      this.formAddEditMARDetails.get("selectedTimes").enable();
      this.formAddEditMARDetails.get("selectedDaysOfTheWeek").enable();
    }

    if (this.areSelectedTimesOrSelectedDaysOfTheWeekPopulated) {
      this.formAddEditMARDetails
        .get("selectedAdministrationFrequency")
        .disable();
    } else {
      this.formAddEditMARDetails
        .get("selectedAdministrationFrequency")
        .enable();
    }
  }

  /**************************************************************************
   * Spinner Methods                                                        *
   **************************************************************************/

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /**************************************************************************
   * Times Components                                                       *
   **************************************************************************/

  get canAddTime(): boolean {
    return (
      this.formAddEditMARDetails.controls["time"].value &&
      this.formAddEditMARDetails.controls["time"].value != undefined
    );
  }

  // Pad times and convert to strings. Do not add duplicates.
  onAddTime() {
    const [hour, minute] = [
      this.formAddEditMARDetails.controls["time"].value.hour,
      this.formAddEditMARDetails.controls["time"].value.minute,
    ].map((time) => `${time}`.padStart(2, "0"));
    this.selectTimesSet.add(`${hour}:${minute}`);
    this.updateTimes();
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
  }

  onRemoveTime(time: NgSelectOption) {
    this.selectTimesSet.delete(time.value);
    this.updateTimes();
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
  }

  onClearTimes() {
    this.selectTimesSet = new Set();
    this.updateTimes();
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
  }

  // Close on open - this prevents the user from using the times dropdown
  onOpenTimes() {
    this.selectorTimes.close();
  }

  // Convert set to array and update the create request
  updateTimes() {
    this.formAddEditMARDetails.controls["selectedTimes"].setValue(
      Array.from(this.selectTimesSet)
    );
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
  }

  /**************************************************************************
   * Days of the Week Components                                            *
   **************************************************************************/

  // For checkboxes for individual days of the week
  isDayOfTheWeekSelected(dropdownItem: DropdownItem): boolean {
    return this.formAddEditMARDetails.controls[
      "selectedDaysOfTheWeek"
    ].value.includes(dropdownItem.value);
  }

  onDaysOfTheWeekClose() {
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    this.selectorDaysOfTheWeek.close();
  }

  get areAllDaysOfTheWeekSelected() {
    return (
      this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].value
        .length === this.propertiesDaysOfTheWeek.dropdownItems!.length
    );
  }

  onClickDaysOfTheWeekSelectAll() {
    if (this.areAllDaysOfTheWeekSelected) {
      this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].setValue([]);
    } else {
      this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].setValue(
        this.propertiesDaysOfTheWeek.dropdownItems!.map(
          (dayOfTheWeek) => dayOfTheWeek.value!
        )
      );
    }
    this.selectorDaysOfTheWeek.close();
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    // this.onSelectedDaysOfTheWeekChange();
  }

  /****************************************************************************
   * Frequency Component                                                      *
   ****************************************************************************/
  onClearAdministrationFrequency() {
    this.formAddEditMARDetails.controls[
      "selectedAdministrationFrequency"
    ].setValue(null);
    this.setIsAdministrationFrequencyPopulated();
  }

  /**************************************************************************
   * Additional Notes Components                                            *
   **************************************************************************/

  // Potentially Not Needed...

  /**************************************************************************
   * Form & Field Validations                                               *
   **************************************************************************/

  setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated() {
    this.areSelectedTimesOrSelectedDaysOfTheWeekPopulated =
      this.formAddEditMARDetails.controls["selectedTimes"].value.length > 0 ||
      this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].value
        .length > 0;
    this.toggleSelectors();
  }

  setIsAdministrationFrequencyPopulated() {
    this.isAdministrationFrequencyPopulated =
      this.formAddEditMARDetails.controls["selectedAdministrationFrequency"]
        .value !== null;
    this.toggleSelectors();
  }

  /**************************************************************************
   * Cancel & Save Buttons                                                  *
   **************************************************************************/

  cancel(): void {
    let alert = this.alertCtrl.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present();
  }

  save(): void {
    // Method State
    let patchAddEditMARDetailsRequest: OrderPatchRequest;

    // Method Behavior
    // Populate All Fields Of `upsertAddEditMARDetails` Object Except Reason
    this.upsertAddEditMARDetails = {
      actionType: Action.ActionTypeEnum.MAREdited,
      displayName: this.userDisplayName,
      MARDaysOfTheWeek:
        this.formAddEditMARDetails.controls["selectedDaysOfTheWeek"].value,
      MARFrequency:
        this.formAddEditMARDetails.controls["selectedAdministrationFrequency"]
          .value,
      MARNotes: this.formAddEditMARDetails.controls["additionalNotes"].value,
      MARTimes: this.formAddEditMARDetails.controls["selectedTimes"].value,
      timezone: moment.tz.guess(),
    };

    // Populate `upsertAddEditMARDetails` Object w/ Reason
    this.upsertAddEditMARDetails.reason = MAREditedAction.getMAREditedReason(
      this.upsertAddEditMARDetails
    );

    // Wrap Object In OrderPatchRequest Object
    patchAddEditMARDetailsRequest = {
      ...this.upsertAddEditMARDetails,
    };

    // Start The Engines
    this.increaseSpinnerCount("Saving ...");

    // Attempt To Save
    this.orderManagementDataService
      .updateOrder(this.order.orderId, patchAddEditMARDetailsRequest)
      .pipe(take(1))
      .subscribe(
        (x) => {
          this.decreaseSpinnerCount();
          this.navCtrl.pop();
        },
        (err) => {
          console.error("Error saving");
          console.error(err);
          this.decreaseSpinnerCount();
          this.presentToast("Error Saving MAR Details");
        }
      );
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/add-edit-mar-details/add-edit-mar-details.ts